import { Check, Share, SquarePlus, type LucideIcon } from "lucide-react";

import { Banner } from "@/components/ui/banner";
import { Card, CardContent } from "@/components/ui/card";
import type { Surface } from "@/lib/surface-detect";

// The Install Gate (Story 7.1). Shown on iOS surfaces that are NOT a manifest-driven PWA
// (safari-proper, chrome-ios, home-screen-bookmark) — the only iOS surface with durable,
// isolated offline storage is the installed PWA, so we guide the clinician through "Add to
// Home Screen" before they create offline-capable work.
//
// Enforcing (gh-469): there is NO "Continue anyway" escape hatch. The only forward path is
// installing/opening the PWA, which re-classifies the surface and clears the gate. The gate
// is purely instructional — it does not classify the surface or own any override.
//
// Presentational only. The caller passes the detected `surface` to tailor the guidance
// copy. Built entirely from design-system primitives + tokens (no hardcoded hex, no
// pure-white canvas), keyboard-accessible, with a semantic h1 → h2 outline.

// AC4 — this exact string must render verbatim.
const EXPLANATORY_COPY =
  "Safari and the home-screen app each have their own offline storage. Use the home-screen app to keep your work safe.";

interface GateStep {
  icon: LucideIcon;
  title: string;
  description: string;
}

const STEPS: readonly GateStep[] = [
  {
    icon: Share,
    title: "Tap Share",
    description: "Open the Share menu from the Safari toolbar at the bottom of the screen.",
  },
  {
    icon: SquarePlus,
    title: "Add to Home Screen",
    description: "Scroll down the Share menu and choose “Add to Home Screen”.",
  },
  {
    icon: Check,
    title: "Tap Add",
    description: "Confirm with “Add”, then open CITS AMPS from your home screen icon.",
  },
];

export interface InstallGateProps {
  /** The non-PWA iOS surface that triggered the gate; tailors the guidance copy. */
  surface?: Surface;
}

export function InstallGate({ surface }: InstallGateProps) {
  // Chrome on iOS is a WKWebView wrapper that cannot "Add to Home Screen" — the steps
  // below only work in Safari, so call that out rather than letting the clinician fail.
  const isChromeIos = surface === "chrome-ios";

  return (
    <main
      aria-labelledby="install-gate-title"
      className="flex min-h-screen flex-col items-center justify-center bg-[color:var(--bg-primary)] px-4 py-10"
    >
      <div className="flex w-full max-w-[480px] flex-col gap-5">
        <header className="flex flex-col gap-2 text-center">
          <h1
            id="install-gate-title"
            className="text-2xl font-semibold leading-tight text-[color:var(--text-primary)]"
          >
            Add CITS AMPS to your home screen
          </h1>
          <p className="text-sm text-[color:var(--text-tertiary)]">
            A one-time step keeps your evaluations safe and available offline.
          </p>
        </header>

        <Banner variant="info">
          <p>{EXPLANATORY_COPY}</p>
        </Banner>

        {isChromeIos ? (
          <Banner variant="warn" title="Open CITS AMPS in Safari first">
            Adding to the Home Screen only works in Safari. Open this page in Safari, then follow the steps below.
          </Banner>
        ) : null}

        <Card>
          <CardContent className="mt-0">
            <ol className="flex flex-col gap-4">
              {STEPS.map((step, index) => (
                <li key={step.title} className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color:var(--bg-brand-primary)] text-[color:var(--text-brand-secondary)]"
                  >
                    <step.icon className="h-5 w-5" />
                  </span>
                  <div className="flex flex-col gap-0.5">
                    <h2 className="text-md font-semibold text-[color:var(--text-primary)]">
                      <span aria-hidden="true" className="mr-1.5 text-[color:var(--text-brand-secondary)]">
                        {index + 1}.
                      </span>
                      {step.title}
                    </h2>
                    <p className="text-sm text-[color:var(--text-secondary)]">{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

export { EXPLANATORY_COPY };
